import React from 'react'
import _ from 'lodash'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import dayjs from 'dayjs'

import Operations from '../../../operations'
import Selectors from '../../../selectors'
import { sizes } from '@lmc/common/constants'
import {
  AntdDateFilter,
  HeaderSection,
  HelpLink
} from '../../../components/generic'
import {
  MedicationRoundsReportTable,
  RoundsReportEmptyState
} from './components'
import {
  EMARLegend,
  EMARPaywall
} from '../../Residents/screens/ResidentEmarScreens/components'
import useFeatureFlags from 'hooks/useFeatureFlags'
import { EmarMedicationRoundsReport } from '../screens/EmarMedicationRoundsReport/EmarMedicationRoundsReport'

const renderHeader = (testId) => (
  <HeaderSection
    title="Medication Rounds"
    data-testid={
      testId ? `${testId}-medication-rounds-header` : 'medication-rounds-header'
    }
  />
)

const renderLegendSection = (testId) => (
  <div
    style={styles.legendContainer}
    data-testid={testId ? `${testId}-legend-section` : 'legend-section'}
  >
    <EMARLegend testId={testId} />
  </div>
)

const renderSubText = (testId) => (
  <div
    style={styles.subtext}
    data-testid={testId ? `${testId}-subtext` : 'subtext'}
  >
    This report only includes the medication rounds that you have set up on the
    Preferences page. For medications at other times, please check the
    individual's eMAR.
  </div>
)

const renderDateFilter = (props) => {
  const { currentDate, setCurrentDate, testId } = props

  return (
    <AntdDateFilter
      currentDate={currentDate}
      setCurrentDate={setCurrentDate}
      wrapFormItem={false}
      data-testid={testId ? `${testId}-date-filter` : 'date-filter'}
    />
  )
}

const renderFilters = (props) => {
  const { testId } = props
  return (
    <div
      style={styles.filterContainer}
      data-testid={testId ? `${testId}-filters` : 'filters'}
    >
      {/* {renderResidentFilter(props)} */}
      {renderDateFilter(props)}
    </div>
  )
}

// End of filter render functions

const renderTable = (props) => {
  const { currentDate, isLoading, tableData, testId } = props

  return (
    <MedicationRoundsReportTable
      currentDate={currentDate}
      isLoading={isLoading}
      rounds={tableData}
      testId={testId}
    />
  )
}

const renderEmptyState = (props) => {
  const { isLoading, tableData } = props

  if (isLoading || (tableData && tableData.length)) return null

  return <RoundsReportEmptyState {...props} />
}

const renderHelpLink = () => (
  <div style={styles.helpLink}>
    <HelpLink supportLink="https://support.logmycare.co.uk/en/collections/2749236-emar" />
  </div>
)

/**
@description Renders the rounds report table.

@param {func} fetchData. Fetches data from Redux.

@param {array} tableData. Array of round objects. Passed to table.

@returns {function} MedicationRoundsReportTable.
*/

export function MedicationRoundsReportScreen(props) {
  const { fetchData, isLoading, tableData, eMAR, testId } = props
  const flags = useFeatureFlags({ flagNames: ['emar_v2_chart'] })?.data
  const emarV2ChartEnabled = flags?.emar_v2_chart?.enabled ?? false

  const [currentDate, setCurrentDate] = React.useState(dayjs())

  React.useEffect(() => {
    if (!eMAR || emarV2ChartEnabled) return
    fetchData(currentDate, emarV2ChartEnabled)
  }, [eMAR, emarV2ChartEnabled, currentDate])

  if (!eMAR) {
    return <EMARPaywall testId={testId} />
  }

  if (emarV2ChartEnabled) {
    return <EmarMedicationRoundsReport />
  }

  const showEmptyState = !isLoading && !(tableData && tableData.length)

  return (
    <div
      style={styles.container}
      data-testid={
        testId
          ? `${testId}-medication-rounds-report-screen`
          : 'medication-rounds-report-screen'
      }
    >
      {renderHeader()}
      {!showEmptyState && renderLegendSection()}
      {!showEmptyState && renderSubText()}
      {!showEmptyState &&
        renderFilters({ ...props, currentDate, setCurrentDate })}
      {!showEmptyState && renderTable({ ...props, currentDate })}
      {showEmptyState && renderEmptyState(props)}
      {renderHelpLink()}
    </div>
  )
}

MedicationRoundsReportTable.propTypes = {
  fetchData: PropTypes.func.isRequired,
  preferredPeopleNominative: PropTypes.string.isRequired,
  tableData: PropTypes.array.isRequired
}

const styles = {
  container: {
    margin: '0 auto',
    maxWidth: sizes.childScreen,
    padding: '24px 24px',
    position: 'relative'
  },
  filterContainer: {
    display: 'flex',
    marginBottom: 32
  },
  helpLink: {
    marginTop: 20
  },
  legendContainer: {
    margin: '8px 0px'
  },
  subtext: {
    lineHeight: '22px',
    marginBottom: 16
  }
}

const REQUEST_ID = 'emar-rounds-chart'
const mapStateToProps = (state, _props) => ({
  isLoading: _.get(state, `data.requests.${REQUEST_ID}.isLoading`),
  preferredPeopleNominative: state.global.home.preferredPeopleNominative,
  residents: state.data.residents,
  tableData: Selectors.getMedicationRoundsReportTable(state, {
    requestID: REQUEST_ID
  }),
  eMAR: state.global.home.features?.eMAR?.enabled
})

const mapDispatchToProps = (dispatch) => ({
  fetchData: (currentDate, emarV2ChartEnabled) => {
    dispatch(Operations.loadList('carers'))
    dispatch(Operations.loadList('residents'))
    dispatch(Operations.loadList('logs'))
    dispatch(
      Operations.fetchData({
        url: emarV2ChartEnabled
          ? '/emar/medication-administration-instances/medication-rounds-report'
          : 'aggregations/emar-dashboard',
        query: {
          residentMeds: true, // flags that we want the extensive report
          from: dayjs(currentDate).startOf('day').toISOString(),
          to: dayjs(currentDate).endOf('day').toISOString()
        },
        id: REQUEST_ID
      })
    )
  }
})

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(MedicationRoundsReportScreen)
